import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { setPotentiallyAuthenticated, useIsPotentiallyAuthenticated } from 'services/auth/auth';
import { useCurrentUser } from 'services/repositories/user/user';
import { readCookie } from 'support/helpers/cookies/cookies';
import { AuthRoutes } from 'support/types';
import { LoadingPage } from 'components/Loading/LoadingPage';

type PrivateRouteProps = {
  children: ReactNode;
  redirectTo?: string;
};

export const PrivateRoute = ({ children, redirectTo = AuthRoutes.login }: PrivateRouteProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useIsPotentiallyAuthenticated();
  const [hasAttemptedAuth, setHasAttemptedAuth] = useState(false);

  // Helper function for redirecting to appropriate authentication page
  const redirectToLogin = useCallback(() => {
    navigate(redirectTo, {
      replace: true,
      state: { from: location.pathname },
    });
  }, [navigate, location.pathname, redirectTo]);

  // Helper function to handle authentication failure
  const handleAuthFailure = useCallback(() => {
    setPotentiallyAuthenticated(false);
    setHasAttemptedAuth(true);
    redirectToLogin();
  }, [redirectToLogin]);

  // Only enable the user query if we're authenticated
  const shouldValidateUser = Boolean(isAuthenticated);

  // Use this to verify the token is actually valid by making an API call
  const {
    isLoading: isValidatingUser,
    isError,
    isSuccess,
  } = useCurrentUser({
    enabled: shouldValidateUser,
    retry: false,
    onError: handleAuthFailure,
    onSuccess: () => {
      setPotentiallyAuthenticated(true);
      setHasAttemptedAuth(true);
    },
  });

  useEffect(() => {
    // Check if the user has an XSRF token and we haven't already attempted auth
    const xsrfToken = readCookie('XSRF-TOKEN');

    // If there's no XSRF token, redirect to login immediately
    if (!xsrfToken) {
      redirectToLogin();
      return;
    }
    // If there's a token but we're not authenticated, haven't tried yet, and have no cached user
    if (!isAuthenticated && !hasAttemptedAuth && !isSuccess) {
      setPotentiallyAuthenticated(true);
      return;
    }

    if (!isAuthenticated) {
      redirectToLogin();
    }
  }, [isAuthenticated, hasAttemptedAuth, isSuccess, redirectToLogin]);

  // Only show content when:
  // 1. User is authenticated AND we're not on login page, OR we're on login page (to show login form)
  // 2. We're not currently validating OR we're on login page
  const shouldRenderChildren = isAuthenticated && !isValidatingUser && !isError && isSuccess;

  return shouldRenderChildren ? (
    <>{children}</>
  ) : (
    <div className="mx-auto h-screen">
      <LoadingPage />
    </div>
  );
};
